// bankroll/bankroll.ts (CX6) - the persistent, SIMULATED play-money bankroll for the "my money on the line"
// Solo experience. Off-table only (North Star §4.4): this is a training SCORE in dollars, not a wager - no
// real money, no gambling. Its whole job is the focus/tension of a roll that carries.
//
// THE ACCOUNTING (exact, and it rides the engine's own honest ledger identity):
//  - The roll is the TOTAL simulated money you own - the chips in your pocket PLUS the chips in front of you
//    on the table. Buying in moves money pocket -> table; it does not change the total. A reload (a rebuild
//    after a bust) moves money pocket -> table too; it does not change the total either. The ONLY thing that
//    moves the total is a hand you WIN or LOSE. So the roll's change over a session === your realized P/L,
//    which is exactly `hand.heroNetChips` summed over the hands you actually played.
//  - That is why Observe and Diagnose cannot touch the roll: only a SOLO-with-bankroll hand is ever settled
//    into it (App calls `applyHandResult` for those hands and no others). A pro auto-playing in Observe moves
//    the session's chips but never this number.
//  - `heroNetChips` (from `LiveHandModel`) is `hero.stack - startStack` for the hand - a fold that loses a
//    blind is negative, a won pot is positive, and a hand where hero busted and reloaded nets to the real
//    loss (the reload tops the table stack back up but the loss already came out of the roll). No separate
//    reload bookkeeping is needed here: the per-hand net already carries it.
//
// SCOPED PER STAKES (CX6 objective, "scoped to the chosen stakes/buy-in"): a $1/$3 roll and a $3/$5 roll are
// separate numbers, because a live player does not carry one blended roll across games. `StakesId` keys the
// store; adding CX8's '5_10' needs only a STARTING_ROLL entry.
//
// PERSISTENCE: localStorage (the "existing persistence" the spec points at - the same tier the session
// snapshot schema was designed for, §7.1; a $0/mo, no-network store). The roll is a handful of numbers, so
// it does not need the /api/sessions D1 path that the graded-decision history uses.
import type { StakesId } from '../home/stakes';
import { STAKES_ORDER } from '../home/stakes';

/** Bump when the stored shape changes; an old/foreign blob then self-evicts to the starting rolls. */
export const BANKROLL_VERSION = 2;

/** The localStorage key for the whole multi-stakes store. */
export const BANKROLL_KEY = 'postflop.bankroll';

/** One stakes level's persisted roll. `balance` is the TOTAL simulated money (pocket + table). */
export interface Roll {
  /** current total, in dollars. Play-money; can be reset. */
  balance: number;
  /** what this roll started (and resets) to - so the readout can show "up/down since you started". */
  startingBalance: number;
  /** how many bankroll hands have settled into it - a light "session depth" signal. */
  handsPlayed: number;
  /** epoch ms of the last change, for a "last played" line. */
  updatedAt: number;
}

/**
 * The starting (and reset) roll per stakes level. Clearly-simulated round numbers, sized to a comfortable
 * number of buy-ins so a normal session does not immediately bust the roll: ~16 buy-ins at $1/$3 (100bb =
 * $300), ~30 at $3/$5 (100bb = $500), and ~30 at $5/$10 (100bb = $1,000). Play-money by construction - the
 * UI labels it as such.
 */
export const STARTING_ROLL: Record<StakesId, number> = {
  '1_3': 5000,
  '3_5': 15000,
  '5_10': 30000,
};

interface Store {
  v: number;
  rolls: Partial<Record<StakesId, Roll>>;
}

/** A fresh roll for a stakes level, at its starting balance. */
export const freshRoll = (id: StakesId, now: number): Roll => ({
  balance: STARTING_ROLL[id],
  startingBalance: STARTING_ROLL[id],
  handsPlayed: 0,
  updatedAt: now,
});

const isRoll = (v: unknown): v is Roll =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as Roll).balance === 'number' &&
  typeof (v as Roll).startingBalance === 'number' &&
  typeof (v as Roll).handsPlayed === 'number' &&
  typeof (v as Roll).updatedAt === 'number';

/** SSR/no-storage guard: the app renders server-side in tests via jsdom, but a hardened build might not
 *  expose localStorage. Every accessor tolerates its absence and behaves like an empty store. */
const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Read the whole store, discarding a version-mismatched or corrupt blob (discard-don't-migrate, §7.3). */
export const loadStore = (): Store => {
  const ls = storage();
  if (!ls) return { v: BANKROLL_VERSION, rolls: {} };
  try {
    const raw = ls.getItem(BANKROLL_KEY);
    if (!raw) return { v: BANKROLL_VERSION, rolls: {} };
    const parsed = JSON.parse(raw) as Store;
    if (!parsed || parsed.v !== BANKROLL_VERSION || typeof parsed.rolls !== 'object') {
      return { v: BANKROLL_VERSION, rolls: {} };
    }
    // keep only well-formed rolls for known stakes ids.
    const rolls: Partial<Record<StakesId, Roll>> = {};
    for (const id of STAKES_ORDER) {
      const r = parsed.rolls[id];
      if (isRoll(r)) rolls[id] = r;
    }
    return { v: BANKROLL_VERSION, rolls };
  } catch {
    return { v: BANKROLL_VERSION, rolls: {} };
  }
};

/** The persisted roll for a stakes level, initializing (and NOT yet saving) a fresh one if none exists. */
export const loadRoll = (id: StakesId, now: number = Date.now()): Roll =>
  loadStore().rolls[id] ?? freshRoll(id, now);

/** Persist one stakes level's roll, leaving the others untouched. */
export const saveRoll = (id: StakesId, roll: Roll): void => {
  const ls = storage();
  if (!ls) return;
  const store = loadStore();
  store.rolls[id] = roll;
  try {
    ls.setItem(BANKROLL_KEY, JSON.stringify(store));
  } catch {
    /* quota/denied - the roll simply does not persist this run; never throw into the UI. */
  }
};

/** Reset a stakes level's roll to its starting balance and persist it. Returns the fresh roll. */
export const resetRoll = (id: StakesId, now: number = Date.now()): Roll => {
  const roll = freshRoll(id, now);
  saveRoll(id, roll);
  return roll;
};

/**
 * Settle ONE played Solo-with-bankroll hand into the roll. PURE.
 *
 * `heroNetChips` is the hand's realized P/L (won pot positive, lost/folded negative). The total roll moves
 * by exactly that - see the module header for why buy-ins and reloads do not enter here. `handsPlayed`
 * increments so the readout can show how deep the session is.
 */
export const applyHandResult = (roll: Roll, heroNetChips: number, now: number = Date.now()): Roll => ({
  ...roll,
  balance: roll.balance + heroNetChips,
  handsPlayed: roll.handsPlayed + 1,
  updatedAt: now,
});

/** Whether the roll can cover a full buy-in to sit with it. A busted/short roll must reset or sit fresh. */
export const canSit = (roll: Roll, buyIn: number): boolean => roll.balance >= buyIn;
